import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { TOKEN_PACKAGE_PRICES } from '@/common/constants/tokenPackagePrices';
import { LanguageContext } from '@/common/contexts/LanguageContext';
import { useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import BuyTokensModalHero from './components/BuyTokensModalHero';
import BuyTokensModalUsage from './components/BuyTokensModalUsage';
import BuyTokensPackageList from './components/BuyTokensPackageList';
import BuyTokensTermsCheckbox from './components/BuyTokensTermsCheckbox';
import BuyTokensCheckoutButton from './components/BuyTokensCheckoutButton';
import { useBuyTokensPayment } from './hooks/useBuyTokensPayment';
import {
	SparkCard,
	SparkClose,
	SparkContent,
	SparkHeader,
	SparkModal,
} from './BuyTokensModal.styles';

const BuyTokensModal = ({
	onClose,
	returnPath = '/chat',
	title,
	description,
	heroTitle,
	heroDescription,
	headerTitle,
}) => {
	const { lang } = useContext(LanguageContext);
	const t = useLanguageData('sparkModal');
	const { user } = useGuestAuth();
	const [selectedKey, setSelectedKey] = useState(null);
	const [termsAccepted, setTermsAccepted] = useState(false);
	const packagePrices = TOKEN_PACKAGE_PRICES[lang] || TOKEN_PACKAGE_PRICES.en;
	const selectedPrice = selectedKey ? packagePrices[selectedKey] : null;

	const { canPurchase, handlePayment, isPending } = useBuyTokensPayment({
		returnPath,
		userId: user?.id,
		userEmail: user?.email,
		selectedKey,
		termsAccepted,
	});

	return createPortal(
		<SparkModal aria-hidden="false" onClick={onClose}>
			<SparkCard
				role="dialog"
				aria-modal="true"
				aria-labelledby="sparkTitle"
				onClick={(event) => event.stopPropagation()}
			>
				<BuyTokensModalHero
					heroTitle={heroTitle}
					heroDescription={heroDescription}
					fallbackTitle={t.premiumAccess}
					fallbackDescription={t.premiumDescription}
				/>
				<SparkContent>
					<SparkClose
						type="button"
						aria-label={t.closeSparkPackages}
						onClick={onClose}
					>
						×
					</SparkClose>
					<SparkHeader>
						<h2 id="sparkTitle">{headerTitle || title || t.selectPackage}</h2>
					</SparkHeader>
					<BuyTokensModalUsage description={description} translations={t} />
					<BuyTokensPackageList
						selectedKey={selectedKey}
						onSelect={setSelectedKey}
						packagePrices={packagePrices}
						packagesLabel={t.sparkPackages}
						packageTranslations={t.packages}
					/>
					<BuyTokensTermsCheckbox
						checked={termsAccepted}
						onChange={(event) => setTermsAccepted(event.target.checked)}
						translations={t}
					/>
					<BuyTokensCheckoutButton
						canPurchase={canPurchase}
						isPending={isPending}
						selectedPrice={selectedPrice}
						onClick={handlePayment}
						translations={t}
					/>
				</SparkContent>
			</SparkCard>
		</SparkModal>,
		document.body,
	);
};

export default BuyTokensModal;
